import nanoid from 'nanoid';
import UrlHelper from 'foundation/url-helper';
import HtmlHelper from 'foundation/html-helper';
import _ from 'lodash';
import gtm from './gtm';
import Modal, { bemClasses } from './modal';
import api, { isHtmlContentType } from './api';
import pageSpinner from './page-spinner';
import historyController from './history-controller';
import { throttle } from './helpers';
import { redirectTo, reloadPage } from './utils';

const MODAL_SPA_ID = 'modal-spa';

const renderType = Object.freeze({
    WINDOW: 'window',
    POPUP: 'popup'
});

class ModalSpa {
    constructor() {
        this.options = {
            url: '',
        };
        this.isOpenning = false;
        this.modalCache = [];
        this.currentModal = null;
        this.unregisterHistroyController = null;

        this.sessionId = nanoid();
        this.chainStartUrl = '';
        this.init();
    }

    init() {
        historyController.register(MODAL_SPA_ID, this.onHistoryChange);
    }

    destroy() {
        if (typeof this.unregisterHistroyController === 'function') {
            this.unregisterHistroyController();
        }
    }

    open(options) {
        this.options = { ...this.options, ...options };
        const openLinkInPopup = this.options.openLinkInPopup !== undefined ? this.options.openLinkInPopup : true;

        if (!this.chainStartUrl) {
            this.chainStartUrl = window.location.href;
        }

        if (!openLinkInPopup) {
            redirectTo(this.options.url);
        } else {
            this.openModal(this.options.url);
            this.setHistoryState(this.options.url);
        }
    }

    openModal(url) {
        if (!this.isOpenning) {
            this.isOpenning = true;
            this.getModal(url).then(([modal, title]) => {
                try {
                    modal.open();
                    this.isOpenning = false;
                    this.currentModal = modal;
                    gtm.sendCurrentLocationAsVirtualUrl(title);
                } catch (error) {
                    throw new Error('Can not open modal.');
                }
            }).catch(() => {
                this.isOpenning = false;
            });
        }
    }

    closeModal() {
        if (this.currentModal) this.currentModal.close();
    }

    getModal(url) {
        const contentUrl = UrlHelper.getOverlayUrl(url);
        return this.getData(contentUrl).then(([target, title]) => {
            try {
                const modal = {};
                modal[url] = [
                    new Modal(target, {
                        previousOpenedModal: this.currentModal,
                        bemList: [bemClasses.MOBILE_FIXED, bemClasses.DEKSTOP_WIDE],
                        delegateControl: true,
                        onCloseCb: () => this.onClose(url)
                    }),
                    title
                ];
                this.modalCache.push(modal);

                return modal[url];
            } catch (error) {
                return Promise.reject(error);
            }
        });
    }

    getData(url) {
        if (!url) return Promise.reject();

        pageSpinner.show();

        return throttle(() => api.get(url)).then((response) => {
            if (isHtmlContentType(response)) {
                let responseHtml = response.data;
                responseHtml = HtmlHelper.modifySocialElementsStyleFromPopupHtml(responseHtml);
                return [responseHtml, HtmlHelper.getTitleFromPopupHtml(responseHtml)];
            }
            return Promise.reject(new Error('Unknown result from async data'));
        }).then((result) => {
            pageSpinner.hide();
            return result;
        }, (error) => {
            console.log(error);

            if (error && error.code && error.code === 429) {
                this.forceHideErrorsSummary();
                this.showThrottleMessage();
            }

            pageSpinner.hide();
            return Promise.reject(error);
        });
    }

    forceHideErrorsSummary() { //this.errorsSummary is undefined in this case
        if (this.refs.serverErrors) {
            this.refs.serverErrors.classList.remove('is-shown');
        }
    }

    showThrottleMessage() {
        if (this.refs.throttleMessage) {
            this.refs.throttleMessage.classList.add('is-shown');
        }
    }

    removeThrottleMessage() {
        if (this.refs.throttleMessage) {
            this.refs.throttleMessage.classList.remove('is-shown');
        }
    }

    setHistoryState(url) {
        historyController.pushState(
            MODAL_SPA_ID,
            null,
            url,
            {
                renderType: renderType.POPUP,
                sessionId: this.sessionId
            }
        );
    }

    setInitialHistoryState() {
        historyController.pushState(
            MODAL_SPA_ID,
            null,
            this.chainStartUrl,
            {
                renderType: renderType.WINDOW,
                sessionId: this.sessionId
            }
        );
        HtmlHelper.removeElementsFromDom('[data-modal]');
        this.chainStartUrl = '';
        this.currentModal = null;
    }

    getPreviousOpenedModalUrl() {
        if (!_.isEmpty(this.modalCache)) {
            const previousOpenedModal = this.modalCache.slice(-1).pop();
            return Object.keys(previousOpenedModal).slice(-1).pop();
        }

        return this.chainStartUrl;
    }

    onClose = (url) => {
        const innerState = historyController.getState();

        if (innerState && innerState.url.toLowerCase() === url.toLowerCase()) {
            this.modalCache.pop();
            if (_.isEmpty(this.modalCache)) {
                this.setInitialHistoryState();
            } else {
                const modalUrl = this.getPreviousOpenedModalUrl();
                this.setHistoryState(modalUrl);
            }
        }
    }

    onHistoryChange = (prevState, newState, shouldClean) => {
        if (shouldClean) {
            this.cleanContent(prevState);
        } else {
            this.updateContent(prevState, newState);
        }
    }

    updateContent(prevState, newState) {
        if (prevState && prevState.sessionId === this.sessionId &&
            newState.sessionId !== this.sessionId
        ) {
            this.closeModal();
            return;
        }

        if (newState.sessionId === this.sessionId) {
            if (newState.renderType === renderType.POPUP) {
                this.openModal(newState.url);
            } else if (newState.renderType === renderType.WINDOW) {
                this.closeModal();
            }
        } else {
            // after page reload flow, we always reload pages
            // instead of to show modal
            reloadPage();
        }
    }

    cleanContent(prevState) {
        if (prevState.sessionId === this.sessionId) {
            this.closeModal();
        } else {
            reloadPage();
        }
    }
}

export default new ModalSpa();
